import { createFileRoute } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from 'react-query';
import { UsersApi } from '@frontend/api-users';
import { MFAContextProvider } from '@frontend/account';
import { authnClient, navigateToUrl } from '@frontend/auth';
import { getWeaveToken } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { http } from '@frontend/fetch';
import { SpinningLoader } from '@frontend/design-system';
import { Layout } from '../../components';
import { MFAPromptFlow } from '../../components/mfa/mfa-prompt-flow';

export const Route = createFileRoute('/mfa-prompt/')({
  // Define the search params type so 'redirectTo' is recognized
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
    };
  },
  component: MFAPromptPage,
  pendingComponent: () => {
    return (
      <Layout background={'blue'}>
        <SpinningLoader key='loader' size='medium' />
      </Layout>
    );
  },
  loader: async () => {
    try {
      const [user, useJTI] = await Promise.all([
        authnClient.fetchUser(), // this will set the user in local storage which parts of the app depend on
        authnClient.shouldUseJTI(), // checks feature flag for JTI
      ]);

      // set up http client for authenticated API calls
      http.setUseJti(useJTI);
      http.setBaseUrl(appConfig.BACKEND_API);
      http.setGlobalSkipValidation(true); // This allows us to bypass Location-Id header validation check
      http.setAuthorizationHeader(getWeaveToken());

      if (user?.userID) {
        const getContactsResponse = await UsersApi.getMFAContacts(user.userID);
        return {
          mfaMethods: getContactsResponse?.contacts || [],
        };
      }
    } catch (error) {
      console.error('Error loading MFA prompt page:', error);
      // the router has an errorComponent that will catch this error and render UI
      // but we'd rather catch it here and redirect to the app
      // redirect to the app for less user friction
      navigateToUrl(appConfig.DEFAULT_REDIRECT, {
        reason: 'MFA post-login prompt failed to load route due to a network error',
        error,
      });
    }
    // should never reach here, but return a default value to satisfy the loader type
    return {
      mfaMethods: [],
    };
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * This page shows the user a prompt to enroll in MFA
 * and a flow to add and verify a new MFA method.
 *
 * This part of gatekeeper differs from the rest because it deals with a logged in user
 */
function MFAPromptPage() {
  const { redirectTo } = Route.useSearch();
  const { mfaMethods } = Route.useLoaderData();

  return (
    <Layout background={'blue'}>
      <QueryClientProvider client={queryClient}>
        <MFAContextProvider>
          <MFAPromptFlow redirectTo={redirectTo} mfaMethods={mfaMethods} />
        </MFAContextProvider>
      </QueryClientProvider>
    </Layout>
  );
}
